/**
 * Reopens the Pandectes cookie consent dialog from a normal navigation link, instead of
 * the floating "reopen" bubble the app renders by default.
 *
 * Pandectes ships that bubble as `<pandectes-cmp>` — a custom element with an *open*
 * declarative shadow root containing `button.widget` (the bubble itself) and the
 * consent `dialog`. Letting the app hide the bubble and wire a menu link to reopen it
 * is only available on Pandectes' paid "Advanced Banner Settings" tier. This script
 * reproduces the same behaviour for free, using only the DOM the app already renders:
 * it hides `button.widget` inside the open shadow root (display: none, so it never
 * paints and drops out of the tab order) and, on click of any link ending in
 * `LINK_HASH`, clicks that same button — the real element, not a copy — which is
 * exactly what a visitor clicking the visible bubble would do.
 *
 * The initial consent banner (first visit, `dialog[open]`) is a separate element and is
 * never touched here, so first-visit consent collection is unaffected.
 *
 * A theme button can trigger the same thing with `data-pandectes-open`
 * (`sections/consent-settings.liquid` on the cookie declaration page).
 *
 * Menu item: Shopify Admin → Online Store → Navigation → add a link whose URL ends in
 * `LINK_HASH` (e.g. `#pandectes-cookie-settings`), label e.g. "Cookie-Einstellungen".
 * Documented in `materials/altdecor-docs/cookie-consent-menu-link.md`.
 */
(() => {
  const HOST_SELECTOR = 'pandectes-cmp';
  const WIDGET_SELECTOR = 'button.widget';
  const LINK_HASH = '#pandectes-cookie-settings';

  const hideWidgetButton = (host) => {
    const button = host.shadowRoot?.querySelector(WIDGET_SELECTOR);
    button?.style.setProperty('display', 'none', 'important');
  };

  const waitForHost = () => new Promise((resolve) => {
    const existing = document.querySelector(HOST_SELECTOR);
    if (existing?.shadowRoot) {
      resolve(existing);
      return;
    }

    const observer = new MutationObserver(() => {
      const host = document.querySelector(HOST_SELECTOR);
      if (host?.shadowRoot) {
        observer.disconnect();
        resolve(host);
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  });

  waitForHost().then((host) => {
    hideWidgetButton(host);
    // The app re-renders the shadow content around consent state changes; keep hiding
    // the bubble whenever that happens instead of hiding it once.
    const shadowObserver = new MutationObserver(() => hideWidgetButton(host));
    shadowObserver.observe(host.shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    });
  });

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(`a[href$="${LINK_HASH}"], [data-pandectes-open]`);
    if (!trigger) return;

    event.preventDefault();
    waitForHost().then((host) => {
      host.shadowRoot.querySelector(WIDGET_SELECTOR)?.click();
    });
  });
})();
